import {
  formatDateTime,
  type JiraConnection,
  jiraConnectionInputSchema,
  type JiraConnectionStatus,
  type JiraTestResult,
  routes,
} from '@pmai/contracts';
import { Alert, Button, Card, PageHeader, Spinner, StatusBadge, TextField } from '@pmai/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApiMutation, useApiQuery } from '../../shared/api/hooks';
import { applyFieldErrors, bind, useZodForm } from '../../shared/forms/form';
import { useErrorText } from '../../shared/i18n';

const TONES: Record<JiraConnectionStatus, 'success' | 'warning' | 'neutral'> = {
  connected: 'success',
  configured: 'warning',
  disconnected: 'neutral',
  not_configured: 'neutral',
};

type Notice = { tone: 'success' | 'warning' | 'danger'; text: string };

/** Iestatījumi → Jira (FR-JIRA-001…006): one connection per workspace, owner/admin only. */
export function JiraSettingsPage() {
  const { t } = useTranslation();
  const connection = useApiQuery(routes.jira.connection);
  const status = connection.data?.status;
  return (
    <>
      <PageHeader
        title={t('jira.title')}
        actions={
          status && (
            <span data-testid="jira-status">
              <StatusBadge tone={TONES[status]} label={t(`jira.status.${status}`)} />
            </span>
          )
        }
      />
      {connection.data ? <JiraForm connection={connection.data} /> : <Spinner label={t('common.loading')} />}
    </>
  );
}

function JiraForm({ connection }: { connection: JiraConnection }) {
  const { t } = useTranslation();
  const errorText = useErrorText();
  const [notice, setNotice] = useState<Notice | null>(null);
  const form = useZodForm(jiraConnectionInputSchema, {
    defaultValues: { baseUrl: connection.baseUrl ?? '', email: connection.email ?? '', token: '' },
  });
  const invalidates = [routes.jira.connection, routes.jira.site];
  const save = useApiMutation(routes.jira.saveConnection, { invalidates });
  const test = useApiMutation(routes.jira.testConnection, { invalidates });
  const disconnect = useApiMutation(routes.jira.disconnect, { invalidates });
  const busy = save.isPending || test.isPending || disconnect.isPending;
  const failed = (error: unknown) => setNotice({ tone: 'danger', text: errorText(error) });

  const onSave = form.handleSubmit((body) => {
    setNotice(null);
    save.mutate(
      { body },
      {
        onSuccess: (saved) => {
          form.reset({ baseUrl: saved.baseUrl ?? '', email: saved.email ?? '', token: '' });
          setNotice({ tone: 'success', text: t('jira.saved') });
        },
        onError: (error) => {
          if (!applyFieldErrors(form, error)) failed(error);
        },
      },
    );
  });
  const onTest = () => {
    setNotice(null);
    test.mutate({}, { onSuccess: (r) => setNotice(testNotice(r)), onError: failed });
  };
  const onDisconnect = () => {
    setNotice(null);
    disconnect.mutate(
      {},
      { onSuccess: () => setNotice({ tone: 'success', text: t('jira.disconnected') }), onError: failed },
    );
  };
  const testNotice = (r: JiraTestResult): Notice => {
    if (r.outcome !== 'ok')
      return { tone: 'danger', text: t(`jira.outcome.${r.outcome}`, { status: r.httpStatus }) };
    const warnings = r.warnings.map((w) => t(`jira.warning.${w}`));
    return {
      tone: warnings.length > 0 ? 'warning' : 'success',
      text: [t('jira.outcome.ok'), ...warnings].join(' '),
    };
  };

  return (
    <Card>
      <form noValidate className="flex max-w-xl flex-col gap-4" onSubmit={(e) => void onSave(e)}>
        <TextField
          label={t('jira.baseUrl')}
          placeholder="https://jusu-vietne.atlassian.net"
          autoComplete="off"
          {...bind(form, 'baseUrl')}
        />
        <TextField label={t('jira.email')} type="email" autoComplete="off" {...bind(form, 'email')} />
        <TextField
          label={t('jira.token')}
          type="password"
          autoComplete="new-password"
          placeholder={connection.hasToken ? t('jira.tokenKept') : t('jira.tokenEnter')}
          hint={connection.hasToken ? t('jira.tokenStoredHint') : t('jira.tokenHint')}
          {...bind(form, 'token')}
        />
        <div className="flex flex-wrap gap-2">
          <Button type="submit" loading={save.isPending} disabled={busy}>
            {t('common.save')}
          </Button>
          <Button variant="secondary" icon="plug" loading={test.isPending} disabled={busy} onClick={onTest}>
            {t('jira.test')}
          </Button>
          {connection.hasToken && (
            <Button variant="ghost" loading={disconnect.isPending} disabled={busy} onClick={onDisconnect}>
              {t('jira.disconnect')}
            </Button>
          )}
        </div>
        {notice && <Alert tone={notice.tone}>{notice.text}</Alert>}
        {(connection.accountName || connection.lastTestedAt) && (
          <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm text-ink-muted">
            {connection.accountName && (
              <>
                <dt>{t('jira.account')}</dt>
                <dd className="font-medium text-ink">{connection.accountName}</dd>
              </>
            )}
            {connection.lastTestedAt && (
              <>
                <dt>{t('jira.lastTested')}</dt>
                <dd className="font-medium text-ink">{formatDateTime(connection.lastTestedAt)}</dd>
              </>
            )}
          </dl>
        )}
      </form>
    </Card>
  );
}
